import React, { useEffect, useState } from 'react'
import { Flex, Modal, Button, StatusBadge, BadgeVariant, MoneyIcon, Checkbox, Text, TextVariant } from '@mprolab/uikit'
import styled, { useTheme } from 'styled-components'
import { useTranslation } from 'react-i18next'
import { RefreshCcw } from 'react-feather'
import {
  useAddRewardToClaimMutation,
  useLazyGetTotalRewardToClaimQuery
} from 'state/services/indexNodeRewards/indexNodeRewards'
import useGetSingleAccessToken from 'hooks/useGetSingleAccessToken'
import { observer } from 'mobx-react-lite'
import { indexNodeStore } from 'mobxStores/IndexNodeStore'
import { useWeb3React } from 'lib/wagmi-compat/useWeb3React'
import { useMproRewardProxyContract } from 'hooks/contracts/useMproRewardProxyContract'
import { useLifetimeTokens } from 'state/global/hooks'
import isEmpty from 'lodash/isEmpty'
import { getErrorCodesDescription } from 'utils/getErrorCodesDescription'
import { BNToNumberWithDecimals, etherToWeiString } from '@mprolab/sdk'
import { logMessage } from 'utils/logMessage'
import { BigNumber } from 'ethers'
import { useGetStakeReward } from '../autoStake.logic'
import { RewardInfoBox, RewardStatus } from './components'
import { ClaimProgress, ClaimRewardActionType, ClaimStatus } from '../types'

const ContentWrapper = styled(Flex)`
  flex-direction: column;
  margin-top: 32px;
  width: 550px;
  gap: 32px;
  ${({ theme }) => theme.mediaQueries.mobile} {
    width: 100%;
  }
`

const EstimatedClaimFeeContainer = styled(Flex)`
  display: flex;
  padding: 8px 16px;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  align-self: stretch;
  border-radius: 8px;
  background: #1a1a1a;
`

const DashedLine = styled(Flex)`
  width: 48px;
  border-top: 1px dashed ${({ theme }) => theme.colors.gray7};

  ${({ theme }) => theme.mediaQueries.mobile} {
    height: 16px;
    border-top: none;
    border-left: 1px dashed ${({ theme }) => theme.colors.gray7};
    margin-left: 24px;
  }
`

const RewardStatusWrapper = styled(Flex)`
  gap: 24px;
  align-items: center;

  ${({ theme }) => theme.mediaQueries.mobile} {
    gap: 8px;
    align-items: flex-start;
    flex-direction: column;
  }
`

interface ClaimHistoryProps {
  mproRewardProxyActiveContractChainId: number
  onDismiss?: () => void
}

const ClaimRewardModal: React.FC<ClaimHistoryProps> = observer(
  ({ onDismiss, mproRewardProxyActiveContractChainId }) => {
    const { t } = useTranslation()
    const { colors } = useTheme()
    const [claimProgress, setClaimProgress] = useState(ClaimProgress.LOADING)
    const [updateRewardStatus, setUpdateRewardStatus] = useState(ClaimStatus.INITIAL)
    const [claimRewardStatus, setClaimRewardStatus] = useState(ClaimStatus.INITIAL)
    const [claimRewardErrorMsg, setClaimRewardErrorMsg] = useState('')
    const [updateRewardErrorMsg, setUpdateRewardErrorMsg] = useState('')
    const [checked, setChecked] = useState(false)
    const [addReward] = useAddRewardToClaimMutation()
    const { account, chainId } = useWeb3React()
    const { obtainAccessToken } = useGetSingleAccessToken()
    const { claimReward, getReward, getMinUpdateTokenAmount, getMintFee } =
      useMproRewardProxyContract()
    const [getRewardFromService] = useLazyGetTotalRewardToClaimQuery()
    const lifetimeTokens = useLifetimeTokens()
    const accessToken = lifetimeTokens?.[account]
    const { totalReward, totalRewardLoading } = useGetStakeReward()
    const [claimRewardTransactionCost, setClaimRewardTransactionCost] = useState(BigNumber.from(0))
    // What one mint actually costs, read from the contract rather than
    // described as a dollar range in a currency it is not charged in.
    const [mintFee, setMintFee] = useState<BigNumber | null>(null)
    const [initialRewardToClaim, setInitialRewardToClaim] = useState(BigNumber.from(0))
    const [minTokenAmountToUpdate, setMinTokenAmountToUpdate] = useState({
      loading: true,
      value: BigNumber.from(0)
    })

    const awaitRewardOnContract = async () => {
      const tries = 30
      let isUpdated = false
      let i = 1
      while (!isUpdated && i <= tries) {
        const reward = await getReward(account, mproRewardProxyActiveContractChainId)

        if (reward._rewardToClaim.gt(initialRewardToClaim)) {
          isUpdated = true
          return reward
        }
        if (i === tries) {
          throw new Error('Cannot fetch reward from contract')
        }
        await new Promise((resolve) => setTimeout(resolve, 1000))
        i++
      }
      return null
    }

    const handleClaimReward = async () => {
      switch (claimProgress) {
        case ClaimProgress.UPDATE_REWARD:
          try {
            setUpdateRewardStatus(ClaimStatus.PENDING)
            setClaimProgress(ClaimProgress.CONFIRMING)
            await obtainAccessToken()
            setClaimProgress(ClaimProgress.PENDING)
            await addReward({
              accessToken: indexNodeStore.accessToken,
              account
            }).unwrap()
            const currentReward = await awaitRewardOnContract()
            setUpdateRewardStatus(ClaimStatus.SUCCESS)
            await getRewardFromService({
              account,
              accessToken
            })
            setClaimRewardTransactionCost(currentReward._claimRewardFee)
            setClaimProgress(ClaimProgress.CLAIM_REWARD)
          } catch (error: any) {
            setUpdateRewardStatus(ClaimStatus.ERROR)
            setClaimProgress(ClaimProgress.ERROR)
            if ((error?.data?.message || '').includes('Cannot add reward before')) {
              setUpdateRewardErrorMsg('You have already claimed the reward for this period')
              setTimeout(() => {
                setClaimProgress(ClaimProgress.CLAIM_REWARD)
                setUpdateRewardErrorMsg('')
              }, 2000)
            } else if (error?.data?.message) {
              // The backend names its reason — an expired session, a refused
              // contract call. Swallowing it into a generic apology is what
              // hid a dead mint path for months: nobody could report an error
              // nobody was shown.
              setUpdateRewardErrorMsg(String(error.data.message))
            } else if (error?.message) {
              setUpdateRewardErrorMsg(getErrorCodesDescription(t, error))
            }
          }
          break
        case ClaimProgress.CLAIM_REWARD:
          try {
            setClaimRewardStatus(ClaimStatus.PENDING)
            setClaimProgress(ClaimProgress.CONFIRMING)
            const tx = await claimReward(chainId)
            setClaimProgress(ClaimProgress.PENDING)
            const confirmation = await tx.wait()
            if (confirmation.status === 1) {
              setClaimRewardStatus(ClaimStatus.SUCCESS)
              setClaimProgress(ClaimProgress.SUCCESS)
            } else {
              setClaimRewardErrorMsg('Something went wrong while claiming the reward, try again in a while')
              setClaimRewardStatus(ClaimStatus.ERROR)
              setClaimProgress(ClaimProgress.ERROR)
            }
          } catch (error: any) {
            if (error.toString().includes('No reward to claim')) {
              setClaimRewardErrorMsg('No reward to claim')
            } else if (error?.message) {
              setClaimRewardErrorMsg(getErrorCodesDescription(t, error))
            }
            setClaimRewardStatus(ClaimStatus.ERROR)
            setClaimProgress(ClaimProgress.ERROR)
          }
          break
        default:
          break
      }
    }

    useEffect(() => {
      getMinUpdateTokenAmount(mproRewardProxyActiveContractChainId)
        .then((minTokenAmount) => {
          setMinTokenAmountToUpdate({ loading: false, value: minTokenAmount })
        })
        .catch((error) => {
          logMessage('Error while fetching min token amount to update', error)
          setMinTokenAmountToUpdate({ loading: false, value: BigNumber.from(0) })
        })
    }, [])

    /**
     * Where the modal opens is decided by the CONTRACT first, the backend
     * second. Minted-but-unclaimed distribution lives on the proxy, and a
     * user who closed this modal between the two steps used to reopen it at
     * the mint step with no way to finish the claim — the operator walked
     * into exactly that trap. The chain read cannot be replaced by the
     * backend figure: the backend only knows what is not yet minted.
     */
    useEffect(() => {
      if (!account || !mproRewardProxyActiveContractChainId || totalRewardLoading) return undefined
      let cancelled = false
      getReward(account, mproRewardProxyActiveContractChainId)
        .then((reward) => {
          if (cancelled) return
          setInitialRewardToClaim(reward._rewardToClaim)
          setClaimRewardTransactionCost(reward._claimRewardFee)
          setClaimProgress((progress) => {
            if (progress !== ClaimProgress.LOADING) return progress
            if (reward._rewardToClaim.gt(0) || totalReward === 0) {
              setUpdateRewardStatus(ClaimStatus.SUCCESS)
              return ClaimProgress.CLAIM_REWARD
            }
            return ClaimProgress.UPDATE_REWARD
          })
        })
        .catch((error) => {
          // An unreachable RPC must not leave the modal on a spinner forever —
          // fall back to the backend-only decision the modal used to make.
          logMessage('Failed to read the initial reward to claim', error)
          if (cancelled) return
          setClaimProgress((progress) => {
            if (progress !== ClaimProgress.LOADING) return progress
            if (totalReward === 0) {
              setUpdateRewardStatus(ClaimStatus.SUCCESS)
              return ClaimProgress.CLAIM_REWARD
            }
            return ClaimProgress.UPDATE_REWARD
          })
        })
      return () => {
        cancelled = true
      }
    }, [account, mproRewardProxyActiveContractChainId, totalReward, totalRewardLoading])

    const errorMessage = () => {
      if (claimProgress === ClaimProgress.ERROR) {
        if (claimRewardErrorMsg) {
          return claimRewardErrorMsg
        }
        return updateRewardErrorMsg
      }
      return ''
    }

    const buttonStatus = () => {
      switch (claimProgress) {
        case ClaimProgress.UPDATE_REWARD:
          const minTokenAmountToUpdateIsWrong = minTokenAmountToUpdate.value.gt(
            etherToWeiString(totalReward.toString())
          )
          return (
            <Flex flexDirection="column" gap="32px" fullWidth>
              <Flex gap="16px">
                <Checkbox checked={checked} onChange={() => setChecked((prev) => !prev)} />
                <Flex flexDirection="column" maxWidth="464px">
                  <Text>
                    {mintFee
                      ? t('stakingPanel.claimWarningWithFee', {
                          fee: BNToNumberWithDecimals(mintFee, 18, 6)
                        })
                      : t('stakingPanel.claimWarning1')}
                  </Text>
                  <Text color={colors.yellow}>{t('stakingPanel.claimWarning2')}</Text>
                </Flex>
              </Flex>
              <Button
                disabled={
                  !checked ||
                  updateRewardStatus === ClaimStatus.PENDING ||
                  minTokenAmountToUpdate.loading ||
                  minTokenAmountToUpdateIsWrong
                }
                onClick={handleClaimReward}
                fullWidth
              >
                {(() => {
                  if (minTokenAmountToUpdate.loading) {
                    return 'Loading...'
                  }
                  return minTokenAmountToUpdateIsWrong
                    ? `Minimum mint reward is ${BNToNumberWithDecimals(minTokenAmountToUpdate.value)} MPRO`
                    : t('stakingPanel.mintReward')
                })()}
              </Button>
              {initialRewardToClaim.gt(0) && (
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setUpdateRewardStatus(ClaimStatus.SUCCESS)
                    setClaimProgress(ClaimProgress.CLAIM_REWARD)
                  }}
                >
                  {t('stakingPanel.finishPendingClaim', {
                    amount: BNToNumberWithDecimals(initialRewardToClaim).toFixed(2)
                  })}
                </Button>
              )}
            </Flex>
          )
        case ClaimProgress.CLAIM_REWARD:
          return (
            <Flex flexDirection="column" gap="32px" fullWidth>
              <EstimatedClaimFeeContainer>
                <Text variant={TextVariant.BODY_SMALL_BOLD} color={colors.gray6}>
                  Claim transaction cost
                </Text>
                <Text variant={TextVariant.BODY_SMALL_BOLD}>
                  {/* Five decimals: at three, an accrued 0.0125 ETH rounds UP
                      to 0.013 and the interface quotes more than is owed. */}
                  {BNToNumberWithDecimals(claimRewardTransactionCost, 18, 6).toFixed(5)} ETH + tx fee
                </Text>
              </EstimatedClaimFeeContainer>

              <Button fullWidth onClick={handleClaimReward} disabled={claimRewardStatus === ClaimStatus.SUCCESS}>
                {t('stakingPanel.claimReward')}
              </Button>
              {totalReward > 0 && claimRewardStatus !== ClaimStatus.SUCCESS && (
                <Button
                  fullWidth
                  variant="text"
                  onClick={() => {
                    setUpdateRewardStatus(ClaimStatus.INITIAL)
                    setClaimProgress(ClaimProgress.UPDATE_REWARD)
                  }}
                >
                  {t('stakingPanel.mintNewDistribution', { amount: totalReward.toFixed(2) })}
                </Button>
              )}
            </Flex>
          )
        case ClaimProgress.CONFIRMING:
          return <StatusBadge title={t('stakingPanel.continueInWallet')} variant={BadgeVariant.PENDING} />
        case ClaimProgress.PENDING:
          return <StatusBadge title={t('transactionPending')} variant={BadgeVariant.PENDING} />
        case ClaimProgress.LOADING:
          return <StatusBadge title="Loading reward data" variant={BadgeVariant.PENDING} />
        case ClaimProgress.SUCCESS:
          return <StatusBadge title={t('rewards.claimSuccess')} variant={BadgeVariant.SUCCESS} />
        case ClaimProgress.ERROR:
          return (
            <StatusBadge
              title={isEmpty(errorMessage()) ? t('globalErrors.somethingWentWrong') : errorMessage()}
              variant={BadgeVariant.ERROR}
            />
          )
        default:
          return <></>
      }
    }

    return (
      <Modal
        onDismiss={onDismiss}
        title={t('stakingPanel.claimingReward')}
        hideCloseButton={claimProgress === ClaimProgress.PENDING}
      >
        <ContentWrapper>
          <RewardStatusWrapper>
            <RewardStatus
              icon={<RefreshCcw size={16} color={colors.white} />}
              status={updateRewardStatus}
              title={t('stakingPanel.mintReward')}
              actionType={ClaimRewardActionType.UPDATE_REWARD}
            />
            <DashedLine />
            <RewardStatus
              icon={<MoneyIcon />}
              status={claimRewardStatus}
              title={t('stakingPanel.claimReward')}
              actionType={ClaimRewardActionType.CLAIM_REWARD}
            />
          </RewardStatusWrapper>
          <Flex gap="8px" justifyContent="center">
            {buttonStatus()}
          </Flex>
          <RewardInfoBox progress={claimProgress} />
        </ContentWrapper>
      </Modal>
    )
  }
)

export default ClaimRewardModal
